import { DEEP_LINK_GPU_COOKIE_KEY } from "@/constants/cluster";
import {
  ClusterType,
  DynamicSupplyHardware,
  DynamicSupplyHardwareFiltersResponse,
  SimpleClusterForm
} from "@/types";
import { findDynamicSupplyGpuByName } from "@/utils/cluster";
import localCookies from "@/utils/cookie";
import { useQueryParams } from "@/hooks/useQueryParams";
import { useFormikContext } from "formik";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { HardwareDetailsModal } from "../HardwareDetailsModal";
import { FilterControls } from "./components/FilterControls";
import { FilterKey } from "./components/filters";
import { HardwareGrid, HIDDEN_HARDWARE_FIELDS } from "./components/HardwareGrid";
import { cn } from "@/lib/utils";
import { VerticalFlowAnalytics } from "../analytics";
import { useSort, type HardwareSortOption } from "./hooks/useSort";
import { RECIPES } from "@/components/ClustersPage/CreateContainerFormV2/steps/RecipesSection.constants";
import {
  findHardwareMatchForRecipe,
  getRecipeMinTotalVramGb,
  hardwareMeetsRecipeVram
} from "@/components/ClustersPage/CreateContainerFormV2/steps/RecipesSection.utils";
import { scrollToHardwareCard } from "./utils/scrollToHardwareCard";

type GPUSelectorProps = {
  onSelectHardware: (hardware: DynamicSupplyHardware) => void;
  /** Double-click on a hardware tile selects it and invokes this with that row (optional). */
  onAdvanceProcessorStep?: (hardware: DynamicSupplyHardware) => void;
  clusterType: ClusterType;
  showSummaryPanel: boolean;
  analytics: VerticalFlowAnalytics;
  fetchHardwareWithFilters: (options?: {
    abortController?: AbortController;
    search?: string;
    filters?: Partial<DynamicSupplyHardwareFiltersResponse>;
  }) => Promise<{
    hardware: DynamicSupplyHardware[];
    filters: DynamicSupplyHardwareFiltersResponse;
  }>;
};

const GPUSelector: React.FC<GPUSelectorProps> = React.memo(
  ({
    onSelectHardware,
    onAdvanceProcessorStep,
    clusterType,
    showSummaryPanel,
    analytics,
    fetchHardwareWithFilters
  }) => {
    const { values, setFieldValue } = useFormikContext<SimpleClusterForm>();
    const [isLoading, setIsLoading] = useState(false);
    const [searchValue, setSearchValue] = useState(values.gpuSearch ?? "");
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [detailsItem, setDetailsItem] = useState<DynamicSupplyHardware | null>(null);
    const [hardware, setHardware] = useState<DynamicSupplyHardware[]>([]);
    const [filtersResponse, setFiltersResponse] =
      useState<DynamicSupplyHardwareFiltersResponse | null>(null);
    const [selectedFilters, setSelectedFilters] =
      useState<null | Partial<DynamicSupplyHardwareFiltersResponse>>(values.gpuFilters ?? null);
    const [showUnavailable, setShowUnavailable] = useState(() => {
      if (values.gpuShowUnavailable !== undefined) {
        return values.gpuShowUnavailable;
      }
      if (values.gpuShowOnlyAvailable !== undefined) {
        return !values.gpuShowOnlyAvailable;
      }
      return false;
    });
    const abortRef = useRef<AbortController | null>(null);
    const requestSeq = useRef(0);
    const searchDebounceRef = useRef<number | null>(null);
    const lastExternalHardwareIdsRef = useRef<string | null>(null);
    const pendingScrollHardwareIdRef = useRef<string | null>(null);
    const userSelectedHardwareRef = useRef(false);
    const valuesHardwareRef = useRef<string | undefined>(undefined);
    const onSelectHardwareRef = useRef(onSelectHardware);
    const { trackApplyFilters, trackGPUSearched, trackProcessorOptionSelected } = analytics;
    const queryParams = useQueryParams<{ hardware_id?: string }>();

    useEffect(() => {
      onSelectHardwareRef.current = onSelectHardware;
    }, [onSelectHardware]);

    useEffect(() => {
      valuesHardwareRef.current = values.hardware;
    }, [values.hardware]);

    const applyHardwareSelection = useCallback(
      (
        hardwareItem: DynamicSupplyHardware,
        options?: { scroll?: boolean; track?: boolean; force?: boolean }
      ) => {
        const alreadySelected = valuesHardwareRef.current === hardwareItem.hardwareId;
        if (alreadySelected && !options?.force && !options?.scroll) {
          if (options?.track) {
            trackProcessorOptionSelected({
              processorId: hardwareItem.hardwareId,
              processorName: hardwareItem.hardwareName
            });
          }
          return;
        }

        if (options?.scroll) {
          pendingScrollHardwareIdRef.current = hardwareItem.hardwareId;
        }
        onSelectHardwareRef.current(hardwareItem);
        if (options?.track) {
          trackProcessorOptionSelected({
            processorId: hardwareItem.hardwareId,
            processorName: hardwareItem.hardwareName
          });
        }
      },
      [trackProcessorOptionSelected]
    );

    const hiddenFilterKeys: FilterKey[] = useMemo(() => {
      if (clusterType === ClusterType.CONTAINER || clusterType === ClusterType.KUBERNETES) {
        return ["supplier", "cpuCores", "deviceMemory", "storage"];
      }
      return [];
    }, [clusterType]);

    const loadHardware = async ({
      currentRequestId,
      gpuSearch,
      filters,
      isFirstLoad = false
    }: {
      currentRequestId: number;
      gpuSearch: string;
      filters: Partial<DynamicSupplyHardwareFiltersResponse> | null;
      isFirstLoad?: boolean;
    }) => {
      if (abortRef.current) {
        try {
          abortRef.current.abort();
        } catch {
          // ignore
        }
      }
      const controller = new AbortController();
      abortRef.current = controller;
      setIsLoading(true);
      try {
        const { hardware, filters: filtersResponse } = await fetchHardwareWithFilters({
          abortController: controller,
          search: gpuSearch,
          filters: filters || undefined
        });
        if (controller.signal.aborted || currentRequestId !== requestSeq.current) {
          return;
        }
        setHardware(hardware);
        if (hardware && hardware.length > 0) {
          const hardwareIds = hardware.map((item) => item.hardwareId).join(",");
          if (lastExternalHardwareIdsRef.current !== hardwareIds) {
            lastExternalHardwareIdsRef.current = hardwareIds;
            setFieldValue("createClusterData", {
              ...(values.createClusterData || {}),
              hardwareList: hardware
            });
          }

          let defaultHardware: DynamicSupplyHardware | null = null;
          const deepLinkHardwareId = queryParams.hardware_id;
          const deepLinkGpu = localCookies.getItem(DEEP_LINK_GPU_COOKIE_KEY);

          if (deepLinkHardwareId) {
            const item = hardware.find((h) => h.hardwareId === deepLinkHardwareId);
            if (item && item.isSoldOut === false) {
              defaultHardware = item;
            }
          }

          if (!defaultHardware && deepLinkGpu) {
            const item = findDynamicSupplyGpuByName(deepLinkGpu, hardware);
            if (item && item.isSoldOut === false) {
              defaultHardware = item;
            }
          }

          if (clusterType === ClusterType.CONTAINER && !valuesHardwareRef.current && values.containerRecipe) {
            const selectedRecipe = RECIPES[values.containerRecipe];
            if (selectedRecipe) {
              const matchedHardware = findHardwareMatchForRecipe(hardware, selectedRecipe);
              if (matchedHardware && matchedHardware.isSoldOut === false) {
                defaultHardware = matchedHardware;
              }
            }
          }

          const currentHardwareId = valuesHardwareRef.current;
          if (currentHardwareId) {
            const item = hardware.find((h) => h.hardwareId === currentHardwareId);
            if (item) {
              defaultHardware = item;
            }
          }

          if (defaultHardware && !userSelectedHardwareRef.current) {
            const shouldAutoSelect =
              isFirstLoad ||
              !currentHardwareId ||
              !hardware.some((item) => item.hardwareId === currentHardwareId);

            if (shouldAutoSelect && currentHardwareId !== defaultHardware.hardwareId) {
              applyHardwareSelection(defaultHardware, { scroll: true, track: true });
            } else if (
              isFirstLoad &&
              currentHardwareId &&
              currentHardwareId === defaultHardware.hardwareId
            ) {
              // Recipe (or draft) already set Formik hardware — sync parent selectedHardware.
              applyHardwareSelection(defaultHardware, { scroll: true, force: true });
            }
          }

          if (
            isFirstLoad &&
            valuesHardwareRef.current &&
            !userSelectedHardwareRef.current &&
            hardware.some((item) => item.hardwareId === valuesHardwareRef.current)
          ) {
            pendingScrollHardwareIdRef.current = valuesHardwareRef.current;
          }
        }
        if (isFirstLoad) {
          setFiltersResponse(filtersResponse);
        }
      } catch (e) {
        // ignore errors and aborts in mock mode
      } finally {
        if (currentRequestId === requestSeq.current) {
          setIsLoading(false);
        }
      }
    };

    const onApplyFilters = (
      overrideFilters?: Partial<DynamicSupplyHardwareFiltersResponse> | null
    ) => {
      const currentRequestId = ++requestSeq.current;
      loadHardware({
        currentRequestId,
        gpuSearch: searchValue,
        filters: overrideFilters ?? selectedFilters
      });
    };

    const clearFilterKey = (key: FilterKey) => {
      setSelectedFilters((prev) => {
        const next = { ...(prev || {}) } as Partial<DynamicSupplyHardwareFiltersResponse>;
        if (key === "gpuFamily") {
          next.gpu_family = [];
        } else if (key === "gpuMemory") {
          next.gpu_memory = undefined;
        } else if (key === "regions") {
          next.regions = [];
        } else if (key === "cpuCores") {
          next.cpu_cores = undefined;
        } else if (key === "deviceMemory") {
          next.device_memory = undefined;
        } else if (key === "storage") {
          next.storage = undefined;
        } else if (key === "supplier") {
          next.supplier = undefined as unknown as DynamicSupplyHardwareFiltersResponse["supplier"];
        } else if (key === "confidentialCompute") {
          next.confidential_compute = [];
        }
        setFieldValue("gpuFilters", next);
        loadHardware({
          currentRequestId: requestSeq.current,
          gpuSearch: searchValue,
          filters: next
        });
        return next;
      });
    };

    const resetFilters = () => {
      trackApplyFilters({ filters: null });
      setSelectedFilters(null);
      setShowUnavailable(false);
      setFieldValue("gpuFilters", null);
      setFieldValue("gpuSearch", "");
      setFieldValue("gpuShowUnavailable", false);
      setFieldValue("gpuSort", "recommended");
      const currentRequestId = ++requestSeq.current;
      loadHardware({ currentRequestId, gpuSearch: "", filters: null });
    };

    const onSearchChange = (value: string) => {
      setSearchValue(value);
      setFieldValue("gpuSearch", value);
      if (searchDebounceRef.current) {
        window.clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
      searchDebounceRef.current = window.setTimeout(() => {
        const currentRequestId = ++requestSeq.current;
        trackGPUSearched({ searchValue: value });
        loadHardware({ currentRequestId, gpuSearch: value, filters: selectedFilters });
      }, 300);
    };

    useEffect(() => {
      const currentRequestId = ++requestSeq.current;
      loadHardware({
        currentRequestId,
        gpuSearch: values.gpuSearch ?? "",
        filters: values.gpuFilters ?? null,
        isFirstLoad: true
      });

      return () => {
        if (abortRef.current) {
          try {
            abortRef.current.abort();
          } catch {
            // ignore
          }
        }
        if (searchDebounceRef.current) {
          window.clearTimeout(searchDebounceRef.current);
          searchDebounceRef.current = null;
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (values.hardware && !userSelectedHardwareRef.current) {
        pendingScrollHardwareIdRef.current = values.hardware;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      const externalHardwareList = (
        values?.createClusterData as unknown as { hardwareList?: DynamicSupplyHardware[] }
      )?.hardwareList as DynamicSupplyHardware[] | undefined;

      if (!Array.isArray(externalHardwareList) || externalHardwareList.length === 0) return;

      const externalHardwareIds = externalHardwareList.map((item) => item.hardwareId).join(",");
      if (lastExternalHardwareIdsRef.current === externalHardwareIds) return;

      lastExternalHardwareIdsRef.current = externalHardwareIds;
      setHardware(externalHardwareList);
    }, [values?.createClusterData]);

    const sortValue = (values.gpuSort ?? "recommended") as HardwareSortOption;

    const selectedRecipe =
      clusterType === ClusterType.CONTAINER && values.containerRecipe
        ? RECIPES[values.containerRecipe]
        : undefined;

    const recipeLockedFilters = useMemo(() => {
      const minTotalVram = selectedRecipe ? getRecipeMinTotalVramGb(selectedRecipe) : undefined;
      if (minTotalVram == null) return [];
      return [
        {
          id: "recipe-min-vram",
          label: `VRAM: ≥ ${minTotalVram} GB`,
          tooltip:
            "Required by your selected recipe. Press Back to change the recipe."
        }
      ];
    }, [selectedRecipe]);

    const { sortedItems: sortedHardware, isSortingEnabled } = useSort(hardware, sortValue);

    const handleSortChange = useCallback(
      (next: HardwareSortOption) => {
        setFieldValue("gpuSort", next);
      },
      [setFieldValue]
    );

    const visibleHardware = useMemo(() => {
      let items = sortedHardware;
      if (selectedRecipe) {
        items = items.filter((item) => hardwareMeetsRecipeVram(item, selectedRecipe));
      }
      if (showUnavailable) return items;
      return items.filter((item) => !item.isSoldOut);
    }, [showUnavailable, sortedHardware, selectedRecipe]);

    useEffect(() => {
      if (userSelectedHardwareRef.current) return;
      if (isLoading) return;
      if (
        values.hardware &&
        visibleHardware.some((item) => item.hardwareId === values.hardware)
      ) {
        return;
      }

      // Deep links and recipe matches are resolved in loadHardware after fetch completes
      if (queryParams.hardware_id) return;

      const firstAvailable = visibleHardware.find((h) => !h.isSoldOut) ?? visibleHardware[0];
      if (!firstAvailable) return;
      if (values.hardware === firstAvailable.hardwareId) return;

      applyHardwareSelection(firstAvailable, { track: true });
    }, [
      visibleHardware,
      values.hardware,
      queryParams.hardware_id,
      isLoading,
      applyHardwareSelection
    ]);

    useEffect(() => {
      const hardwareId = pendingScrollHardwareIdRef.current;
      if (!hardwareId || isLoading) return;

      const scrollTargetId = hardwareId;
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          scrollToHardwareCard(scrollTargetId, clusterType, {
            behavior: "auto",
            onComplete: (success) => {
              if (success && pendingScrollHardwareIdRef.current === scrollTargetId) {
                pendingScrollHardwareIdRef.current = null;
              }
            }
          });
        });
      });
    }, [isLoading, values.hardware, hardware.length, clusterType]);

    const handleSelectHardware = (hardwareId: string) => {
      const item = hardware.find((h) => h.hardwareId === hardwareId);
      if (!item || item.isSoldOut) return;
      if (valuesHardwareRef.current === hardwareId) return;

      userSelectedHardwareRef.current = true;
      pendingScrollHardwareIdRef.current = null;
      applyHardwareSelection(item, { track: true });
    };

    return (
      <>
        <div
          className={cn(
            "flex flex-1 flex-col gap-4 max-lg:w-full lg:pb-0",
            showSummaryPanel && "_lg:pb-10"
          )}
        >
          <FilterControls
            searchValue={searchValue}
            onSearchChange={onSearchChange}
            isSortingEnabled={isSortingEnabled}
            sortValue={sortValue}
            onSortChange={handleSortChange}
            filters={selectedFilters}
            filtersResponse={filtersResponse}
            hiddenFilterKeys={hiddenFilterKeys}
            hideMoreFilters={clusterType === ClusterType.CONTAINER}
            onClearKey={clearFilterKey}
            onChangeSelected={(next) => {
              setSelectedFilters(next);
              setFieldValue("gpuFilters", next);
            }}
            onApplyFilters={onApplyFilters}
            onResetFilters={resetFilters}
            showUnavailable={showUnavailable}
            onShowUnavailableChange={(val) => {
              setShowUnavailable(val);
              setFieldValue("gpuShowUnavailable", val);
            }}
            onTrackApplyFilters={(filters) => trackApplyFilters({ filters })}
            lockedFilters={recipeLockedFilters}
          />
          <HardwareGrid
            searchValue={searchValue}
            selectedFilters={selectedFilters}
            showSummaryPanel={showSummaryPanel}
            isLoading={isLoading}
            items={visibleHardware}
            selectedId={values.hardware ?? null}
            onSelect={handleSelectHardware}
            onAdvanceProcessorStep={onAdvanceProcessorStep}
            onShowDetails={(hw) => {
              setDetailsItem(hw);
              setDetailsOpen(true);
            }}
            clusterType={clusterType}
            analytics={analytics}
            onResetFilters={() => {
              setSearchValue("");
              setFieldValue("gpuSearch", "");
              userSelectedHardwareRef.current = false;
              setFieldValue("hardware", "");
              resetFilters();
            }}
          />
        </div>
        <HardwareDetailsModal
          isOpen={detailsOpen}
          hardware={detailsItem}
          onClose={() => setDetailsOpen(false)}
          analytics={analytics}
          hiddenHardwareFields={
            HIDDEN_HARDWARE_FIELDS[clusterType as keyof typeof HIDDEN_HARDWARE_FIELDS]
          }
        />
      </>
    );
  }
);

export default GPUSelector;
